import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import Sidebar from './Sidebar';
import Loader from '../layout/loader';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import './adminRoutes.css';

function SellerPlansAdmin() {
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [editForm, setEditForm] = useState({
        price: '',
        productLimit: '',
        commissionRate: '',
    });
    const [saving, setSaving] = useState(false);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/admin/vendor/plans');
            setPlans(data.plans || []);
        } catch (error) {
            swal({
                title: "Error",
                text: error.response?.data?.msg || "Failed to load seller plans.",
                icon: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    const startEdit = (plan) => {
        setEditingPlanId(plan._id);
        setEditForm({
            price: plan.price,
            productLimit: plan.productLimit,
            commissionRate: plan.commissionRate
        });
    };

    const cancelEdit = () => {
        setEditingPlanId(null);
    };

    const handleSave = async (id) => {
        try {
            setSaving(true);
            const { data } = await axios.put(`/api/admin/vendor/plan/${id}`, {
                price: Number(editForm.price),
                productLimit: Number(editForm.productLimit),
                commissionRate: Number(editForm.commissionRate)
            });
            swal({
                title: "Success",
                text: data.message || "Seller plan updated successfully.",
                icon: "success"
            });
            setEditingPlanId(null);
            fetchPlans();
        } catch (error) {
            swal({
                title: "Update Failed",
                text: error.response?.data?.msg || "Unable to update seller plan.",
                icon: "error"
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Fragment>
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-container admin-dashboard-main">
                    <section className="admin-dashboard-hero">
                        <div>
                            <span className="admin-dashboard-eyebrow">MARKETPLACE REVENUE &amp; TIERS</span>
                            <h1>Seller Plans &amp; Commission Rates</h1>
                            <p>Configure subscription pricing, product listing limits, and marketplace commission percentages for each seller tier.</p>
                        </div>
                        <button type="button" className="admin-refresh-button" onClick={fetchPlans} disabled={loading}>
                            <RefreshIcon />
                            <span>Refresh</span>
                        </button>
                    </section>

                    {loading ? (
                        <Loader />
                    ) : (
                        <div style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                            {plans.map((plan) => {
                                const isEditing = editingPlanId === plan._id;
                                const isGrowth = plan.code === 'growth';

                                return (
                                    <div
                                        key={plan._id}
                                        className="admin-panel"
                                        style={{
                                            border: isGrowth ? '2px solid #a85050' : '1px solid #e0e4ea',
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            padding: '28px 24px'
                                        }}
                                    >
                                        {isGrowth && (
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    top: '-12px',
                                                    left: '24px',
                                                    background: '#a85050',
                                                    color: '#ffffff',
                                                    fontSize: '10px',
                                                    fontWeight: 900,
                                                    letterSpacing: '1px',
                                                    textTransform: 'uppercase',
                                                    padding: '3px 10px',
                                                    borderRadius: '99px'
                                                }}
                                            >
                                                MOST POPULAR
                                            </span>
                                        )}

                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#faedea', color: '#a85050', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <CardMembershipOutlinedIcon />
                                                    </div>
                                                    <div>
                                                        <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#111315', margin: 0 }}>
                                                            {plan.name} Plan
                                                        </h3>
                                                        <span style={{ fontSize: '11px', color: '#737a84', fontWeight: 700, textTransform: 'uppercase' }}>
                                                            Code: {plan.code}
                                                        </span>
                                                    </div>
                                                </div>

                                                {!isEditing && (
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(plan)}
                                                        style={{
                                                            background: '#f0f2f5',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            padding: '6px 10px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: 800,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            color: '#111315'
                                                        }}
                                                    >
                                                        <EditIcon style={{ fontSize: '14px' }} /> Edit
                                                    </button>
                                                )}
                                            </div>

                                            {isEditing ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '18px 0', background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                                    <div>
                                                        <label style={{ fontSize: '12px', fontWeight: 800, color: '#4f5661', display: 'block', marginBottom: '4px' }}>
                                                            Monthly Subscription Price (₹)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            value={editForm.price}
                                                            onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #d0d7de', fontSize: '14px', fontWeight: 700 }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '12px', fontWeight: 800, color: '#4f5661', display: 'block', marginBottom: '4px' }}>
                                                            Product Listing Limit
                                                        </label>
                                                        <input
                                                            type="number"
                                                            value={editForm.productLimit}
                                                            onChange={(e) => setEditForm({ ...editForm, productLimit: e.target.value })}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #d0d7de', fontSize: '14px', fontWeight: 700 }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: '12px', fontWeight: 800, color: '#4f5661', display: 'block', marginBottom: '4px' }}>
                                                            Marketplace Commission Rate (%)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            value={editForm.commissionRate}
                                                            onChange={(e) => setEditForm({ ...editForm, commissionRate: e.target.value })}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #d0d7de', fontSize: '14px', fontWeight: 700 }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', margin: '20px 0', textAlign: 'center' }}>
                                                    <div style={{ background: '#fbfaf8', padding: '12px 8px', borderRadius: '8px', border: '1px solid #edf0f4' }}>
                                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Price</div>
                                                        <div style={{ fontSize: '18px', fontWeight: 900, color: '#111315', marginTop: '2px' }}>
                                                            {plan.price === 0 ? '₹0 Free' : `₹${plan.price}/mo`}
                                                        </div>
                                                    </div>
                                                    <div style={{ background: '#fbfaf8', padding: '12px 8px', borderRadius: '8px', border: '1px solid #edf0f4' }}>
                                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Limit</div>
                                                        <div style={{ fontSize: '18px', fontWeight: 900, color: '#111315', marginTop: '2px' }}>
                                                            {plan.productLimit} items
                                                        </div>
                                                    </div>
                                                    <div style={{ background: '#fbfaf8', padding: '12px 8px', borderRadius: '8px', border: '1px solid #edf0f4' }}>
                                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Commission</div>
                                                        <div style={{ fontSize: '18px', fontWeight: 900, color: plan.commissionRate === 0 ? '#16a34a' : '#a85050', marginTop: '2px' }}>
                                                            {plan.commissionRate}%
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{ borderTop: '1px solid #edf0f4', paddingTop: '14px', marginTop: '10px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 800, color: '#4f5661', marginBottom: '8px' }}>
                                                    Included Features:
                                                </div>
                                                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#5a5854', lineHeight: '1.6' }}>
                                                    {(plan.features || []).map((feat, idx) => (
                                                        <li key={idx}>{feat}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        {isEditing && (
                                            <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid #edf0f4', paddingTop: '14px', marginTop: '14px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSave(plan._id)}
                                                    disabled={saving}
                                                    style={{
                                                        flex: 1,
                                                        background: '#16a34a',
                                                        color: '#ffffff',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 12px',
                                                        fontWeight: 800,
                                                        fontSize: '13px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <CheckIcon style={{ fontSize: '16px' }} /> Save Changes
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    disabled={saving}
                                                    style={{
                                                        background: '#f0f2f5',
                                                        color: '#111315',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '8px 12px',
                                                        fontWeight: 800,
                                                        fontSize: '13px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <CloseIcon style={{ fontSize: '16px' }} /> Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </main>
            </div>
        </Fragment>
    );
}

export default SellerPlansAdmin;
